import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal } from '../../ui/Modal';
import { DayLabels, ServiceLabels } from '@census/shared';
import type { AiSuggestion, AiSuggestionAction } from '@census/shared';
import { fmtTime } from '../../shared/dates';
import { api } from '../../shared/api';

// ─── Preview data shape ────────────────────────────────────────────────────
// Typed to match what POST /scheduling/preview actually returns.
// IntakePreview.tsx holds a looser (any-typed) local version; these types
// give the checklist helpers IntelliSense without touching the parent file.

interface ClientDraft {
  cohort: string;
  cohortId: string;
  firstName: string;
  lastName: string;
  iopTrackResourceId: string | null;
  iopTrackName: string | null;
  nursingGroupDay: string | null;
  requiredServices: string[];
  intakeDate: string;
}

interface GroupEnrollment {
  groupResourceId: string;
  serviceType: string;
  displayName?: string;
  days: string[];
  startMin: number;
  endMin: number;
  placeOfService: string;
}

interface DecisionTrace {
  serviceType: string;
  placed: boolean;
  day?: string;
  startMin?: number;
  endMin?: number;
  resourceName?: string;
  reasons: string[];
  alternativesConsidered: number;
  score?: number;
}

interface ResourceAudit {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  capacity: number;
  weeklyMinutesBefore: number;
  weeklyMinutesAfter: number;
  weeklyMinutesCap: number;
  utilizationBefore: number;
  utilizationAfter: number;
  proposedSlots: Array<{
    day: string;
    startMin: number;
    endMin: number;
    serviceType: string;
  }>;
}

interface Violation {
  code: string;
  severity: 'HARD' | 'POLICY' | 'SOFT';
  message: string;
  serviceType?: string;
  resourceId?: string;
  day?: string;
}

export interface PreviewData {
  clientDraft: ClientDraft;
  groupEnrollments: GroupEnrollment[];
  proposedAppointments: Array<{
    serviceType: string;
    resourceId: string;
    resourceName?: string;
    day: string;
    startMin: number;
    endMin: number;
    placeOfService: string;
    viaTelehealth?: boolean;
  }>;
  decisionTraces: DecisionTrace[];
  resourceAudits: ResourceAudit[];
  violations: Violation[];
  unscheduledServices: string[];
}

// ─── Checklist types ──────────────────────────────────────────────────────

type CheckStatus = 'pass' | 'warn' | 'fail' | 'info';

interface CheckItem {
  status: CheckStatus;
  label: string;
  detail?: string;
}

// ─── Utilities ────────────────────────────────────────────────────────────

function svcLabel(serviceType: string): string {
  return (ServiceLabels as Record<string, string>)[serviceType] ?? serviceType;
}

function dayLabel(day: string): string {
  return (DayLabels as Record<string, string>)[day] ?? day;
}

export function countViolationsBySeverity(violations: Violation[]) {
  return {
    hard: violations.filter((v) => v.severity === 'HARD').length,
    policy: violations.filter((v) => v.severity === 'POLICY').length,
    soft: violations.filter((v) => v.severity === 'SOFT').length,
  };
}

// ─── Section builders ─────────────────────────────────────────────────────

function buildGroupChecks(preview: PreviewData): CheckItem[] {
  const items: CheckItem[] = [];
  const { groupEnrollments, violations, clientDraft } = preview;

  const missingViolations = violations.filter(
    (v) => v.code === 'MISSING_REQUIRED_GROUP_RESOURCE',
  );

  // Overall enrollment count
  if (groupEnrollments.length === 0 && missingViolations.length === 0) {
    items.push({ status: 'info', label: 'No group services required for this cohort' });
  } else {
    items.push({
      status: groupEnrollments.length > 0 ? 'pass' : 'fail',
      label:
        groupEnrollments.length > 0
          ? `${groupEnrollments.length} group enrollment(s) assigned for Cohort ${clientDraft.cohort}`
          : `No group enrollments assigned for Cohort ${clientDraft.cohort}`,
    });
  }

  // One row per enrolled group
  for (const g of groupEnrollments) {
    const daysStr = g.days.map(dayLabel).join(', ');
    const timeStr = `${fmtTime(g.startMin)}–${fmtTime(g.endMin)}`;
    items.push({
      status: 'pass',
      label: `${svcLabel(g.serviceType)} assigned`,
      detail: `${g.displayName ?? g.serviceType} · ${daysStr} · ${timeStr}`,
    });
  }

  // Hard: missing required groups
  for (const v of missingViolations) {
    items.push({
      status: 'fail',
      label: `Required group missing: ${v.serviceType ? svcLabel(v.serviceType) : 'unknown'}`,
      detail: v.message,
    });
  }

  // IOP track selection
  const hasIop = groupEnrollments.some((g) => g.serviceType === 'IOP');
  if (hasIop) {
    items.push(
      clientDraft.iopTrackName
        ? {
            status: 'pass' as const,
            label: `IOP track selected: ${clientDraft.iopTrackName}`,
          }
        : {
            status: 'info' as const,
            label: 'IOP track auto-selected by engine (lowest enrollment)',
          },
    );
  }

  // Nursing group day
  const hasNursing = groupEnrollments.some((g) => g.serviceType === 'NURSING');
  if (hasNursing) {
    items.push(
      clientDraft.nursingGroupDay
        ? {
            status: 'pass' as const,
            label: `Nursing group day: ${dayLabel(clientDraft.nursingGroupDay)}`,
          }
        : {
            status: 'warn' as const,
            label: 'Nursing group day not explicitly assigned',
          },
    );
  }

  // Policy: group capacity
  for (const v of violations.filter((v) => v.code === 'GROUP_OVER_CAPACITY')) {
    items.push({ status: 'warn', label: 'Group at capacity', detail: v.message });
  }

  // Policy: understaffed
  for (const v of violations.filter((v) => v.code === 'GROUP_UNDERSTAFFED')) {
    items.push({ status: 'warn', label: 'Group understaffed', detail: v.message });
  }

  return items;
}

function buildProviderChecks(preview: PreviewData): CheckItem[] {
  const items: CheckItem[] = [];
  const { decisionTraces, unscheduledServices, resourceAudits, violations } = preview;

  if (decisionTraces.length === 0) {
    items.push({ status: 'info', label: 'No individual services to evaluate' });
  }

  // One row per decision trace (individual service)
  for (const trace of decisionTraces) {
    if (trace.placed) {
      const parts: string[] = [];
      if (trace.resourceName) parts.push(trace.resourceName);
      if (trace.day && trace.startMin !== undefined && trace.endMin !== undefined) {
        parts.push(`${dayLabel(trace.day)} ${fmtTime(trace.startMin)}–${fmtTime(trace.endMin)}`);
      }
      items.push({
        status: 'pass',
        label: `${svcLabel(trace.serviceType)} assigned`,
        detail: parts.join(' · ') || undefined,
      });
    } else {
      items.push({
        status: 'fail',
        label: `${svcLabel(trace.serviceType)} — could not be placed`,
        detail: trace.reasons[0],
      });
    }
  }

  // Unscheduled services that don't appear in traces (defensive)
  const tracedUnplacedSet = new Set(
    decisionTraces.filter((t) => !t.placed).map((t) => t.serviceType),
  );
  for (const svc of unscheduledServices) {
    if (!tracedUnplacedSet.has(svc)) {
      items.push({ status: 'fail', label: `${svcLabel(svc)} — unscheduled (no engine trace)` });
    }
  }

  // Provider utilization warnings from resource audits
  for (const audit of resourceAudits) {
    if (audit.weeklyMinutesCap <= 0) continue;
    if (audit.utilizationAfter > 100) {
      items.push({
        status: 'warn',
        label: `${audit.resourceName} exceeds weekly hour cap`,
        detail: `${audit.utilizationAfter.toFixed(0)}% utilized (cap: ${(audit.weeklyMinutesCap / 60).toFixed(0)}h/week)`,
      });
    } else if (audit.utilizationAfter > 90) {
      items.push({
        status: 'warn',
        label: `${audit.resourceName} approaching weekly cap`,
        detail: `${audit.utilizationBefore.toFixed(0)}% → ${audit.utilizationAfter.toFixed(0)}% (soft cap: 90%)`,
      });
    }
  }

  // Double-booking (should never appear — engine blocks these — but surface if present)
  const dblViolations = violations.filter(
    (v) => v.code === 'CLIENT_DOUBLE_BOOK' || v.code === 'PROVIDER_DOUBLE_BOOK',
  );
  if (dblViolations.length > 0) {
    for (const v of dblViolations) {
      items.push({
        status: 'fail',
        label: v.code.replace(/_/g, ' '),
        detail: v.message,
      });
    }
  } else {
    items.push({
      status: 'info',
      label: 'No double-booking detected',
      detail: 'Availability and overlap rules are enforced server-side by the scheduling engine',
    });
  }

  return items;
}

function buildConflictChecks(preview: PreviewData): CheckItem[] {
  const items: CheckItem[] = [];
  const { violations } = preview;
  const counts = countViolationsBySeverity(violations);

  // Hard
  if (counts.hard === 0) {
    items.push({ status: 'pass', label: 'No hard blockers — commit is permitted' });
  } else {
    items.push({
      status: 'fail',
      label: `${counts.hard} hard blocker(s) found — commit is blocked`,
    });
    for (const v of violations.filter((vv) => vv.severity === 'HARD')) {
      items.push({ status: 'fail', label: v.code, detail: v.message });
    }
  }

  // Policy
  if (counts.policy === 0) {
    items.push({ status: 'pass', label: 'No policy warnings' });
  } else {
    items.push({
      status: 'warn',
      label: `${counts.policy} policy warning(s) — admin override reason required`,
    });
    for (const v of violations.filter((vv) => vv.severity === 'POLICY')) {
      items.push({ status: 'warn', label: v.code, detail: v.message });
    }
  }

  // Soft
  if (counts.soft === 0) {
    items.push({ status: 'pass', label: 'No soft warnings' });
  } else {
    items.push({
      status: 'info',
      label: `${counts.soft} soft warning(s) — informational only, do not block commit`,
    });
    for (const v of violations.filter((vv) => vv.severity === 'SOFT')) {
      items.push({ status: 'info', label: v.code, detail: v.message });
    }
  }

  return items;
}

// ─── Final recommendation ─────────────────────────────────────────────────

type RecommendationStatus = 'ready' | 'needs-override' | 'not-ready';

interface Recommendation {
  status: RecommendationStatus;
  summary: string;
  detail: string;
}

export function getFinalSchedulingRecommendation(preview: PreviewData): Recommendation {
  const { hard, policy, soft } = countViolationsBySeverity(preview.violations);
  const unscheduledCount = preview.unscheduledServices.length;

  if (hard > 0 || unscheduledCount > 0) {
    const issues: string[] = [];
    if (hard > 0) issues.push(`${hard} hard violation(s)`);
    if (unscheduledCount > 0) issues.push(`${unscheduledCount} unscheduled service(s)`);
    return {
      status: 'not-ready',
      summary: 'Not ready to commit',
      detail: `This schedule cannot be committed. Resolve the following before proceeding: ${issues.join(', ')}.`,
    };
  }

  if (policy > 0) {
    return {
      status: 'needs-override',
      summary: 'Needs admin override',
      detail: `The schedule is valid — no hard blockers were found. However, there ${policy === 1 ? 'is' : 'are'} ${policy} policy warning(s) that require an admin override reason. You will be prompted for a reason when you click "Commit intake".`,
    };
  }

  const placed = preview.decisionTraces.filter((t) => t.placed).length;
  const softNote =
    soft > 0 ? ` ${soft} soft note(s) are present but do not block commit.` : '';
  return {
    status: 'ready',
    summary: 'Ready to commit',
    detail: `All required services and groups have been assigned with no violations. ${placed} individual service(s) scheduled across ${preview.groupEnrollments.length} group enrollment(s).${softNote}`,
  };
}

// ─── Rendering primitives ─────────────────────────────────────────────────

const STATUS_ICON: Record<CheckStatus, string> = {
  pass: '✅',
  warn: '⚠️',
  fail: '❌',
  info: 'ℹ️',
};

function CheckRow({ item }: { item: CheckItem }) {
  // Subtle background + left accent for non-pass rows so blockers/warnings
  // visually stand out in a long list of green checks. Pass/info rows stay
  // on a plain white background — they're the "expected, fine" case.
  const tone =
    item.status === 'fail'
      ? 'bg-rose-50/50 border-l-2 border-l-rose-400'
      : item.status === 'warn'
        ? 'bg-amber-50/50 border-l-2 border-l-amber-400'
        : 'border-l-2 border-l-transparent';
  return (
    <div className={`flex gap-3 px-3 py-2 ${tone}`}>
      <span className="mt-0.5 shrink-0 select-none text-sm leading-5" aria-hidden="true">
        {STATUS_ICON[item.status]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{item.label}</p>
        {item.detail && (
          <p className="mt-0.5 text-xs text-ink-subtle">{item.detail}</p>
        )}
      </div>
    </div>
  );
}

function CheckSection({
  title,
  items,
  defaultOpen = false,
}: {
  title: string;
  items: CheckItem[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const fails = items.filter((i) => i.status === 'fail').length;
  const warns = items.filter((i) => i.status === 'warn').length;
  const passes = items.filter((i) => i.status === 'pass').length;
  const allClear = fails === 0 && warns === 0;
  return (
    <div className="overflow-hidden rounded-lg border border-line-subtle bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex w-full flex-wrap items-center justify-between gap-3 bg-surface-subtle/60 px-3 py-2 text-left transition-colors hover:bg-surface-subtle ${
          open ? 'border-b border-line-subtle' : ''
        }`}
      >
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            {allClear ? (
              <span className="pill-success">All clear</span>
            ) : (
              <>
                {fails > 0 && (
                  <span className="pill-danger">
                    {fails} blocker{fails === 1 ? '' : 's'}
                  </span>
                )}
                {warns > 0 && (
                  <span className="pill-warn">
                    {warns} warning{warns === 1 ? '' : 's'}
                  </span>
                )}
                {passes > 0 && (
                  <span className="pill-muted">{passes} ok</span>
                )}
              </>
            )}
          </div>
          <svg
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-ink-subtle"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {open ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}
          </svg>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-line-subtle">
          {items.map((item, i) => (
            <CheckRow key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

const RECOMMENDATION_CONFIG: Record<
  RecommendationStatus,
  { containerCls: string; badgeCls: string; icon: string }
> = {
  ready: {
    containerCls: 'border border-emerald-200 bg-emerald-50',
    badgeCls: 'pill-success',
    icon: '✅',
  },
  'needs-override': {
    containerCls: 'border border-amber-200 bg-amber-50',
    badgeCls: 'pill-warn',
    icon: '⚠️',
  },
  'not-ready': {
    containerCls: 'border border-rose-200 bg-rose-50',
    badgeCls: 'pill-danger',
    icon: '❌',
  },
};

// ─── AI narrative panel ───────────────────────────────────────────────────

/**
 * Resolve an action's target (resourceName) against the current preview so we
 * can both (a) check whether it's already been applied and (b) hand the parent
 * a resolved resourceId. Returns null when a referenced provider can't be
 * found in this preview's audits — the Apply button stays disabled in that case.
 */
function resolveActionTarget(
  action: AiSuggestionAction,
  preview: PreviewData,
): { resourceId?: string } | null {
  if (action.kind === 'EXCLUDE_RESOURCE') {
    const target = preview.resourceAudits.find(
      (a) => a.resourceName.trim().toLowerCase() === action.resourceName.trim().toLowerCase(),
    );
    if (!target) return null;
    return { resourceId: target.resourceId };
  }
  // EXCLUDE_DAY and AVOID_OVERLAP need no resource resolution.
  return {};
}

/** Stable key for "have we applied this action?" lookups. */
function actionKey(action: AiSuggestionAction, resourceId?: string): string {
  if (action.kind === 'EXCLUDE_DAY') return `EXCLUDE_DAY|${action.serviceType}|${action.day}`;
  if (action.kind === 'AVOID_OVERLAP') return `AVOID_OVERLAP|${action.serviceType}`;
  return `EXCLUDE_RESOURCE|${action.serviceType}|${resourceId ?? action.resourceName}`;
}

function isActionApplied(
  action: AiSuggestionAction,
  serviceConstraints: ServiceConstraintsLite | undefined,
  resourceId: string | undefined,
): boolean {
  if (!serviceConstraints) return false;
  if (action.kind === 'EXCLUDE_DAY') {
    return !!serviceConstraints.excludedDaysByService?.[action.serviceType]?.includes(action.day);
  }
  if (action.kind === 'AVOID_OVERLAP') {
    return !!serviceConstraints.avoidOverlapServices?.includes(action.serviceType);
  }
  if (!resourceId) return false;
  return !!serviceConstraints.excludedResourcesByService?.[action.serviceType]?.includes(resourceId);
}

// Locally typed mirror of the shared ServiceConstraints — keeps this file
// usable without importing the zod-derived type (which the parent owns).
interface ServiceConstraintsLite {
  excludedDaysByService?: Record<string, string[] | undefined>;
  excludedResourcesByService?: Record<string, string[] | undefined>;
  pinnedDayByService?: Record<string, string | undefined>;
  pinnedResourceByService?: Record<string, string | undefined>;
  avoidOverlapServices?: string[];
}

function AiNarrativePanel({
  narrative,
  suggestions,
  suggestionsValidating,
  isPending,
  isError,
  preview,
  serviceConstraints,
  onApply,
  onRetry,
}: {
  narrative?: string;
  suggestions?: AiSuggestion[];
  // When true, AI returned suggestions but we're still checking which ones
  // are actually applicable. Show a small "Checking…" placeholder instead of
  // a possibly-flickering list.
  suggestionsValidating?: boolean;
  isPending: boolean;
  isError: boolean;
  preview: PreviewData;
  serviceConstraints?: ServiceConstraintsLite;
  onApply?: (actions: AiSuggestionAction[]) => void | Promise<void>;
  onRetry?: () => void;
}) {
  if (isPending) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-line-subtle bg-slate-50 px-4 py-5">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
        <p className="flex-1 text-sm text-ink-subtle">GPT-4o is analyzing your schedule…</p>
        {onRetry && (
          <button
            type="button"
            className="text-2xs font-medium text-violet-700 underline hover:text-violet-900"
            onClick={onRetry}
            title="If this is taking too long, click to send the request again"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <span className="flex-1">
          ⚠️ AI analysis is temporarily unavailable. The checklist below is still accurate — it is generated directly from the scheduling engine output.
        </span>
        {onRetry && (
          <button
            type="button"
            className="shrink-0 rounded border border-amber-300 bg-white px-2 py-0.5 text-2xs font-medium text-amber-800 hover:bg-amber-100"
            onClick={onRetry}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!narrative) return null;

  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;

  // Annotate each suggestion with whether it's machine-applicable and whether
  // it's already in effect on the current draft.
  const annotated = (suggestions ?? []).map((s) => {
    if (!s.action) return { s, applicable: false as const, applied: false, resourceId: undefined as string | undefined };
    const target = resolveActionTarget(s.action, preview);
    if (!target) return { s, applicable: false as const, applied: false, resourceId: undefined };
    return {
      s,
      applicable: true as const,
      applied: isActionApplied(s.action, serviceConstraints, target.resourceId),
      resourceId: target.resourceId,
    };
  });
  const pendingActions = annotated.filter((a) => a.applicable && !a.applied);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-4">
        <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-indigo-400">GPT-4o Analysis</p>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{narrative}</p>
      </div>

      {suggestionsValidating && (
        <div className="flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
          <p className="text-2xs text-violet-700">
            Checking which suggestions can be applied to the current schedule…
          </p>
        </div>
      )}

      {hasSuggestions && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-2xs font-semibold uppercase tracking-wider text-violet-500">
              💡 Suggested Adjustments
            </p>
            {onApply && pendingActions.length > 1 && (
              <button
                className="rounded bg-violet-600 px-2.5 py-1 text-2xs font-medium text-white hover:bg-violet-700"
                onClick={() => onApply(pendingActions.map((a) => a.s.action!).filter(Boolean))}
                title="Apply every machine-applicable suggestion and re-run the schedule"
              >
                Apply all ({pendingActions.length})
              </button>
            )}
          </div>
          <ul className="space-y-2">
            {annotated.map((a, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-ink">
                <span className="mt-0.5 shrink-0 text-violet-400">→</span>
                <span className="min-w-0 flex-1">{a.s.text}</span>
                {onApply && a.applicable && (
                  a.applied ? (
                    <span
                      className="shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-2xs font-medium text-emerald-700"
                      title="This adjustment is already in effect on the current preview"
                    >
                      Applied ✓
                    </span>
                  ) : (
                    <button
                      className="shrink-0 rounded border border-violet-300 bg-white px-2 py-0.5 text-2xs font-medium text-violet-700 hover:bg-violet-50"
                      onClick={() => onApply([a.s.action!])}
                      title="Apply this adjustment and re-run the schedule"
                    >
                      Apply
                    </button>
                  )
                )}
                {onApply && a.s.action && !a.applicable && (
                  <span
                    className="shrink-0 text-2xs italic text-ink-subtle"
                    title="The referenced provider isn't in the current preview — can't auto-apply"
                  >
                    n/a
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Claude analysis panel ────────────────────────────────────────────────

interface ClaudeReviewData {
  narrative: string;
  suggestions: AiSuggestion[];
  model: string;
}

function ClaudeNarrativePanel({
  preview,
  gptNarrative,
  gptSuggestions,
  gptSettled,
  previewEpoch,
  onStatusChange,
}: {
  preview: PreviewData;
  gptNarrative: string | undefined;
  gptSuggestions: AiSuggestion[] | undefined;
  /** GPT-4o has finished (success OR error) — NOT merely "not pending". This
   *  avoids the initial-idle render where the mutation hasn't fired yet. */
  gptSettled: boolean;
  /** Bumped on every fresh preview run — keys the query so each is independent. */
  previewEpoch: number;
  /** Reactive: called whenever AI-complete flips. true = GPT done AND Claude settled. */
  onStatusChange?: (complete: boolean) => void;
}) {
  const gptRef = useRef({ narrative: gptNarrative, suggestions: gptSuggestions });
  gptRef.current = { narrative: gptNarrative, suggestions: gptSuggestions };

  const cd = preview.clientDraft;
  const claudeQuery = useQuery<ClaudeReviewData>({
    // previewEpoch makes each preview run a fresh query — no stale cache hit
    // from a prior review of the same patient.
    queryKey: ['claude-review', previewEpoch, cd.firstName, cd.lastName, cd.cohort, cd.cohortId],
    // Only fire once GPT-4o has actually settled (so Claude reviews its output,
    // and never starts during the brief pre-mutation idle render).
    enabled: gptSettled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    queryFn: () =>
      api
        .post<ClaudeReviewData>(
          '/scheduling/claude-review',
          {
            preview,
            gptNarrative: gptRef.current.narrative ?? '',
            gptSuggestions: gptRef.current.suggestions ?? [],
          },
          { timeout: 120_000, _suppressGlobalError: true } as any,
        )
        .then((r) => r.data),
  });

  // Reactive completion signal: true only when GPT-4o has settled AND Claude
  // has settled. If GPT re-runs (Retry / safety-net), gptSettled flips false
  // and this flips back to false — so the parent's Commit button re-disables.
  const aiComplete = gptSettled && (claudeQuery.isSuccess || claudeQuery.isError);
  useEffect(() => {
    onStatusChange?.(aiComplete);
  }, [aiComplete, onStatusChange]);

  // Waiting for GPT-4o to finish
  if (!gptSettled) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-100 bg-amber-50 px-4 py-4">
        <span className="h-4 w-4 rounded-full border-2 border-amber-200" />
        <p className="text-sm text-ink-subtle">Claude review waiting for GPT-4o…</p>
      </div>
    );
  }

  // Claude request in flight (covers the brief gap before fetchStatus updates)
  if (!claudeQuery.isError && !claudeQuery.data) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-100 bg-amber-50 px-4 py-5">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-amber-300 border-t-amber-700" />
        <p className="flex-1 text-sm text-ink-subtle">Claude is reviewing the GPT-4o analysis…</p>
      </div>
    );
  }

  if (claudeQuery.isError) {
    const msg =
      (claudeQuery.error as any)?.response?.data?.message ??
      (claudeQuery.error as any)?.message ??
      'Claude review failed';
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        <span className="flex-1">⚠️ Claude review failed: {msg}</span>
        <button
          type="button"
          className="shrink-0 rounded border border-rose-300 bg-white px-2 py-0.5 text-2xs font-medium text-rose-800 hover:bg-rose-100"
          onClick={() => claudeQuery.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  const data = claudeQuery.data!;
  if (!data.narrative) return null;
  const hasSuggestions = Array.isArray(data.suggestions) && data.suggestions.length > 0;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-4">
        <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-amber-500">
          Claude Review of GPT-4o Analysis
        </p>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{data.narrative}</p>
      </div>
      {hasSuggestions && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
          <p className="mb-3 text-2xs font-semibold uppercase tracking-wider text-amber-500">
            💡 Claude Suggestions
          </p>
          <ul className="space-y-2">
            {data.suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-ink">
                <span className="mt-0.5 shrink-0 text-amber-400">→</span>
                <span>{s.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  preview: PreviewData;
  // Currently-applied draft constraints. Drives the "Applied ✓" marker so a
  // suggestion that's already in effect can't be applied twice.
  serviceConstraints?: ServiceConstraintsLite;
  // Parent merges these actions into draft.serviceConstraints and re-previews.
  // Returns true when the change was accepted (no schedule regression) and
  // false when refused — the modal uses that to decide whether to auto-close.
  // Omit to render suggestions as read-only (no Apply buttons).
  onApplySuggestions?: (actions: AiSuggestionAction[]) => Promise<boolean>;
}

/**
 * Thin Modal wrapper around <AiSchedulingChecklistContent>. The body is
 * extracted so the intake Preview screen can render the same checklist
 * inline (without a modal). When used as a modal, a successful apply also
 * closes — refused applies keep the modal open.
 */
export function AiSchedulingChecklistModal({
  open,
  onClose,
  preview,
  serviceConstraints,
  onApplySuggestions,
}: Props) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="AI Scheduling Checklist"
      subtitle={
        preview?.clientDraft
          ? `${preview.clientDraft.cohortId} — ${preview.clientDraft.firstName} ${preview.clientDraft.lastName} · Cohort ${preview.clientDraft.cohort}`
          : undefined
      }
      size="lg"
    >
      <AiSchedulingChecklistContent
        preview={preview}
        serviceConstraints={serviceConstraints}
        onApplySuggestions={
          onApplySuggestions
            ? async (actions) => {
                const ok = await onApplySuggestions(actions);
                if (ok) onClose();
                return ok;
              }
            : undefined
        }
      />
    </Modal>
  );
}

/**
 * Inline body of the AI Scheduling Checklist — AI narrative + suggestions +
 * engine checklists + final recommendation. Renders without a modal wrapper
 * so it can be embedded directly on a page (e.g. the intake Preview screen).
 * Fires the AI review once when mounted.
 */
export function AiSchedulingChecklistContent({
  preview,
  serviceConstraints,
  onApplySuggestions,
  validateApplicability,
  previewEpoch = 0,
  onAiStatusChange,
}: {
  preview: PreviewData;
  serviceConstraints?: ServiceConstraintsLite;
  onApplySuggestions?: (actions: AiSuggestionAction[]) => Promise<boolean>;
  validateApplicability?: (action: AiSuggestionAction) => Promise<boolean>;
  /** Bumped per preview run; threaded into the Claude query key. */
  previewEpoch?: number;
  /** Reactive AI-complete signal (true = GPT done AND Claude settled). */
  onAiStatusChange?: (complete: boolean) => void;
}) {
  if (!preview?.clientDraft || !Array.isArray(preview.violations)) {
    return (
      <p className="py-8 text-center text-sm text-ink-subtle">
        Preview data is unavailable. Run a scheduling preview first.
      </p>
    );
  }

  const aiReview = useMutation({
    mutationFn: () =>
      api
        .post<{ narrative: string; suggestions: AiSuggestion[]; model: string }>(
          '/scheduling/ai-review',
          { preview },
          // 90s safety net — without this, an upstream stall (proxy hiccup,
          // OpenAI delay, etc.) would leave the panel showing "analyzing…"
          // indefinitely. After this, it flips to an error state so the
          // operator at least sees a reason and can retry.
          { timeout: 90_000 },
        )
        .then((r) => r.data),
  });

  // Fire once on mount. We deliberately don't refire on every preview change
  // (e.g. after Apply) to keep OpenAI costs predictable; the suggestions may
  // show as stale after applying — operator can click Retry to refresh.
  const firedRef = useRef(false);
  useEffect(() => {
    if (!firedRef.current) {
      firedRef.current = true;
      aiReview.mutate();
    }
  }, []);

  // Safety net: if the request is still pending after 30s, fire it again.
  // This unwedges cases where the in-flight request stalls in the browser
  // proxy without producing an error event (otherwise the panel would
  // appear "analyzing…" forever).
  useEffect(() => {
    if (!aiReview.isPending) return;
    const t = setTimeout(() => {
      if (aiReview.isPending) aiReview.mutate();
    }, 30_000);
    return () => clearTimeout(t);
  }, [aiReview.isPending]);

  const retry = () => aiReview.mutate();

  // Pre-validate each AI suggestion against the engine — hides suggestions
  // whose action would create new HARD blockers, so the operator never sees
  // an Apply button that would refuse. Runs once each time the AI returns a
  // new set of suggestions; suggestions with action=null are always kept.
  // applicabilityMap = null → validation in flight (or not started yet).
  const [applicabilityMap, setApplicabilityMap] = useState<Record<number, boolean> | null>(null);
  useEffect(() => {
    const sugs = aiReview.data?.suggestions;
    if (!sugs || sugs.length === 0) {
      setApplicabilityMap(null);
      return;
    }
    if (!validateApplicability) {
      // No validator wired (e.g. modal use without a parent dry-run) — keep all.
      setApplicabilityMap(Object.fromEntries(sugs.map((_, i) => [i, true])));
      return;
    }
    setApplicabilityMap(null);
    let cancelled = false;
    Promise.all(
      sugs.map(async (s, i) => {
        if (!s.action) return [i, true] as const;
        try {
          const ok = await validateApplicability(s.action);
          return [i, ok] as const;
        } catch {
          return [i, false] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setApplicabilityMap(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [aiReview.data?.suggestions, validateApplicability]);

  const suggestionsValidating =
    !!aiReview.data?.suggestions && !!validateApplicability && applicabilityMap === null;
  const filteredSuggestions = (aiReview.data?.suggestions ?? []).filter((_, i) =>
    applicabilityMap ? applicabilityMap[i] !== false : true,
  );

  const groupChecks = buildGroupChecks(preview);
  const providerChecks = buildProviderChecks(preview);
  const rec = getFinalSchedulingRecommendation(preview);
  const cfg = RECOMMENDATION_CONFIG[rec.status];

  return (
    <div className="space-y-5">
      <AiNarrativePanel
        narrative={aiReview.data?.narrative}
        // Don't render suggestions until validation completes — otherwise the
        // operator briefly sees suggestions that then disappear. While the AI
        // is generating, suggestions is undefined; once back, we pass either
        // undefined (still validating) or the filtered applicable subset.
        suggestions={
          aiReview.data && !suggestionsValidating ? filteredSuggestions : undefined
        }
        suggestionsValidating={suggestionsValidating}
        isPending={aiReview.isPending}
        isError={aiReview.isError}
        preview={preview}
        serviceConstraints={serviceConstraints}
        onApply={
          onApplySuggestions
            ? async (actions) => {
                await onApplySuggestions(actions);
              }
            : undefined
        }
        onRetry={retry}
      />

      <ClaudeNarrativePanel
        preview={preview}
        gptNarrative={aiReview.data?.narrative}
        gptSuggestions={aiReview.data?.suggestions}
        gptSettled={aiReview.isSuccess || aiReview.isError}
        previewEpoch={previewEpoch}
        onStatusChange={onAiStatusChange}
      />

      {/* Side-by-side on tablets/desktops; stacks on small screens. Keeps
          each subsection compact so the whole checklist fits without much
          scrolling. */}
      <div className="grid gap-4 md:grid-cols-2">
        <CheckSection title="Group Assignment" items={groupChecks} />
        <CheckSection title="Provider Assignment" items={providerChecks} />
      </div>

      {/* Engine Recommendation — final verdict, full-width below the checks. */}
      <div className={`flex items-start gap-3 rounded-lg border p-4 ${cfg.containerCls}`}>
        <span className="mt-0.5 shrink-0 text-lg" aria-hidden="true">
          {cfg.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">Engine Recommendation</h3>
            <span className={cfg.badgeCls}>{rec.summary}</span>
          </div>
          <p className="text-sm leading-relaxed text-ink">{rec.detail}</p>
        </div>
      </div>
    </div>
  );
}
